import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useImmer } from "use-immer";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { PersonalizationTestType, TabEnumFe } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { StartFromScratchCards } from "@src/features/offers/components/StartFromScratchCards";
import { DiscountSyncErrorBanner } from "@src/features/personalizations/pages/personalizationOverview/DiscountSyncErrorBanner";
import { ExperiencesTable } from "@src/features/personalizations/pages/personalizationOverview/ExperiencesTable";
import { SharedTableHeader } from "@src/features/sharedExperience/components/SharedTableHeader";
import { useViewPage } from "@src/hooks/mixpanel";
import { PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { useDispatch } from "@src/store";
import { setDialog } from "@src/store/slices/global";
import { mpClickCreatePersonalizationButton } from "@src/utils/mixpanel";

export const ExperiencesBody = () => {
  const dispatch = useDispatch();
  const [statusFilters, setStatusFilters] = useImmer<string[]>([]);
  const [typeFilters, setTypeFilters] = useImmer<PersonalizationTestType[]>([]);
  const [searchInput, setSearchInput] = useImmer<string>("");
  const isAdmin = useIsAdmin();
  const personalizations = useGetExperiencesList({
    category: "personalization",
  });

  const shopifyFunctionsEnabled = useConfiguration<boolean>(
    "shopify_functions_enabled"
  );

  const onCheckoutScriptClick = () => {
    dispatch(
      setDialog({
        dialog: "checkoutScript",
        open: true,
        props: { isTest: false },
      })
    );
  };

  const hasDiscountSyncError = useMemo(() => {
    if (personalizations.isSuccess) {
      return personalizations.data.experiencesList.some(
        (p) => p.variations[0]?.functionSyncError
      );
    }
    return false;
  }, [personalizations.isSuccess]);
  useViewPage("Experiences");
  return (
    <Grid container rowSpacing={8}>
      {hasDiscountSyncError && (
        <Grid item xs={12}>
          <DiscountSyncErrorBanner />
        </Grid>
      )}
      <SharedTableHeader
        endButtons={
          <Grid item container direction={"row"} columnSpacing={2}>
            {isAdmin && !shopifyFunctionsEnabled?.value ? (
              <Grid item>
                <IgButton
                  size={"small"}
                  variant={"outlined"}
                  onClick={onCheckoutScriptClick}
                >
                  Get Checkout Script (admin only)
                </IgButton>
              </Grid>
            ) : null}
            <Grid item>
              <Link
                style={{ textDecoration: "none" }}
                to={`${PERSONALIZATION_ROUTES.Personalization}/?action=new&tab=${TabEnumFe.MODS}`}
                state={{
                  showDetailsDialog: true,
                }}
                onClick={() => {
                  mpClickCreatePersonalizationButton();
                }}
              >
                <IgButton size={"small"} variant="contained">
                  New Experience
                </IgButton>
              </Link>
            </Grid>
          </Grid>
        }
        title={"Experiences"}
        experienceData={personalizations}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        statusFilters={statusFilters}
        setStatusFilters={setStatusFilters}
        typeFilters={typeFilters}
        setTypeFilters={setTypeFilters}
        statusFilterListOptions={["Active", "Inactive", "Archived"]}
        typeFilterListOptions={
          [
            "Content Edits",
            "Theme",
            "Template",
            "Price",
            "Redirects",
            "Offers",
          ] as PersonalizationTestType[]
        }
      />
      <ExperiencesTable
        searchInput={searchInput}
        personalizationData={personalizations}
        statusFilters={statusFilters}
        typeFilters={typeFilters}
      />
    </Grid>
  );
};
